import {
  Box,
  Card,
  CardContent,
  Skeleton,
  useMediaQuery,
} from "@mui/material";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import AnalyticsWidgetHeader from "./AnalyticsWidgetHeader";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  DASHBOARD_CHART_WIDGET_HEIGHT,
} from "./dashboardWidgetLayout";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { GetStationExpenseResponse } from "subModule/src/services/analytics/interface";
import { useGetStationExpenseWeeklyService } from "subModule/src/services/analytics/useAnalytics";
import i18next, { TFunction } from "i18next";
import { LineChart } from "@mui/x-charts/LineChart";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";
import { InfiniteData } from "@tanstack/react-query";
interface ExpensesWeeklyProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}
export default function ExpensesWeekly(props: ExpensesWeeklyProps) {
  const { t } = useTranslation();
  const firstRender = useRef(true);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: "weekly", label: t("wtd") },
      allStation: false,
    },
  });
  const values = watch(["stationId", "range", "allStation"]);
  const { refetch: apiRefetch } = useGetStationExpenseWeeklyService({
    stationId: values[0]?.value || null,
    range: values[1]?.value,
    allStation: values[2],
  });
  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stations]);
  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("allStation", props.globalFilters.allStation);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);
  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetStationExpenseResponse | unknown | any) => {
        if (res?.data?.success) {
          setData(res?.data?.data || {});
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };
  return (
    <Card
      sx={{
        height: DASHBOARD_CHART_WIDGET_HEIGHT,
        minHeight: DASHBOARD_CHART_WIDGET_HEIGHT,
        marginTop: "16px",
        boxShadow: "none",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        "& .MuiCardContent-root": {
          padding: "16px !important",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          overflow: "hidden",
        },
      }}
    >
      <CardContent>
        <Box sx={dashboardWidgetHeaderSx}>
          <AnalyticsWidgetHeader
            title={t("weeklyExpenses")}
            icon={<CalendarMonthOutlined />}
            iconBg={`${props.themes?.primary || "#4F46E5"}18`}
            iconColor={props.themes?.primary || "#4F46E5"}
          />
          <Box sx={dashboardWidgetFiltersSx}>
          <Controller
            name="stationId"
            control={control}
            defaultValue={null}
            rules={{ required: t("This field is required.") }}
            render={({ field }) => (
              <Box
                sx={{
                  "& .css-7896aj-control, & .css-1xiok1u-control": {
                    minHeight: "auto",
                    fontSize: '9px',
                    height: "auto",
                    overflow: "hidden",
                    "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                      {
                        background: "#fff",
                        padding: 0,
                        width: "15px",
                        height: "15px",
                      },
                    "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator":
                      { position: "absolute", right: 0 },
                    border: 0,
                  },
                }}
              >
                <DynamicSelect
                  label={""}
                  {...field}
                  options={props?.stations || []}
                  isDisabled={loading}
                  onChange={(selectedOption: any) => {
                    field.onChange(selectedOption);
                    if (selectedOption?.value === 0)
                      setValue("allStation", true);
                    else setValue("allStation", false);
                    setTimeout(() => fetchData(), 50);
                  }}
                  error={errors.stationId?.message as string}
                  ddWidth="30px"
                  ddHeight="30px"
                  defaultPlaceholder={t("station")}
                  isLoading={props.searchLoading === "expensesWeekly"}
                  onInputChange={(
                    search: string,
                    { action }: { action: string },
                  ) => {
                    if (
                      action === "input-change" ||
                      (action !== "set-value" &&
                        action !== "menu-close" &&
                        action !== "input-blur")
                    )
                      props.handleDropdownActions(
                        "expensesWeekly",
                        "searchIt",
                        search,
                      );
                    else if (
                      action === "input-blur" ||
                      action === "menu-close"
                    ) {
                      if (props.dropdownSearchTerm || props.pageNo > 1)
                        props.handleDropdownActions(
                          "expensesWeekly",
                          "searchIt",
                          "",
                        );
                      else props.setDropdownSearchTerm("");
                      props.setPageNo(1);
                    }
                  }}
                  onMenuScrollToBottom={() => {
                    if (!(props.stationsData?.pages[0]?.data as any).last)
                      props.handleDropdownActions("expensesWeekly", "loadNext");
                  }}
                  page={props.pageNo}
                />
              </Box>
            )}
          />
          </Box>
        </Box>
        {loading ? (
          <>
            <Skeleton
              animation="pulse"
              sx={{
                backgroundColor: props?.themes?.secondary,
                marginTop: "10px",
              }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
          </>
        ) : (
          <>
            <Expenses data={data} themes={props.themes} {...{ t }} />
          </>
        )}
      </CardContent>
    </Card>
  );
}
const Expenses = ({
  data,
  themes,
  t,
}: {
  data: Record<string, number>;
  themes?: any;
  t: TFunction<"translation", undefined>;
}) => {
  const isLargeScreen = useMediaQuery(`@media('min-width: 1200px')`);
  const formattedData = Object.entries(data || {}).map(([dateStr, value]) => {
    const weekday = new Date(dateStr).toLocaleDateString("en", {
      weekday: "short",
    });
    return {
      label: t(weekday),
      fullLabel: dateStr,
      value,
    };
  });
  return (
    <Box>
      <LineChart
        height={310}
        xAxis={[
          {
            data: formattedData.map((item) => item.label),
            scaleType: "point",
            valueFormatter: (label, context) =>
              `${
                context.location === "tooltip"
                  ? new Intl.DateTimeFormat(i18next.language, {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      numberingSystem:
                        i18next.language === "ur" || i18next.language === "ar"
                          ? "arab"
                          : "latn",
                    }).format(
                      new Date(
                        formattedData.find((d) => d.label === label)
                          ?.fullLabel || ("" as any),
                      ),
                    )
                  : label
              }`,
          },
        ]}
        series={[
          {
            data: formattedData.map((item) => item.value),
            label: t("expense"),
          },
        ]}
        yAxis={[
          {
            valueFormatter: (val) =>
              new Intl.NumberFormat("en", {
                notation: "compact",
                compactDisplay: "short",
                maximumFractionDigits: 2,
              }).format(Number(val)),
          },
        ]}
        skipAnimation={false}
        margin={{
          left: 40,
          right: isLargeScreen ? 0 : 12,
          top: 25,
          bottom: 45,
        }}
        colors={[themes?.secondary]}
        grid={{ horizontal: true }}
        slotProps={{
          legend: { hidden: true },
          noDataOverlay: {
            message: t("noRecordFound"),
          },
        }}
      />
    </Box>
  );
};
